import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { cartAPI } from '../lib/api';
import { getLocalCount, CART_EVENT } from '../lib/localCart';
import { ShoppingCart, Package, User, LogOut, Menu, X, LayoutDashboard, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function Navbar() {
    const { user, logout, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [cartCount, setCartCount] = useState(0);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        // Se evalúa TAMBIÉN al montar, no solo en cada evento: si la página
        // carga ya desplazada (el navegador restaura la posición al recargar,
        // o se vuelve atrás desde otra página), no llega ningún evento de
        // scroll y la cabecera se quedaba en tamaño de reposo sobre una página
        // scrolleada hasta que el usuario moviera la rueda. Verificado en
        // producción: con scrollY=600 las clases seguían siendo las de reposo,
        // y bastaba disparar un 'scroll' a mano para que se corrigieran.
        handleScroll();
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // El contador refleja el carrito del servidor con sesión, y el del
    // navegador sin ella. CART_EVENT lo actualiza al instante cuando se
    // agrega algo desde una ficha de producto, sin esperar a cambiar de página.
    useEffect(() => {
        const refreshCount = () => {
            if (user) {
                cartAPI.get().then(res => setCartCount(res.data.count)).catch(() => {});
            } else {
                setCartCount(getLocalCount());
            }
        };
        refreshCount();
        window.addEventListener(CART_EVENT, refreshCount);
        return () => window.removeEventListener(CART_EVENT, refreshCount);
    }, [user, location]);

    const navLinks = [
        { id: 'catalogo', to: '/catalogo', label: 'Catálogo' },
        { id: 'sectores', to: '/papel-para', label: 'Sectores' },
        { id: 'guias', to: '/guias', label: 'Guías' },
    ];

    return (
        <>
        <header
            data-testid="navbar"
            /* `fixed`, no `sticky`, para poder COMPACTARSE al hacer scroll.
               Con `sticky` la cabecera vive en el flujo: al encogerla, el
               documento se acorta y todo el contenido de abajo salta hacia
               arriba (se midió: 64px de salto). Sacándola del flujo y dejando
               en su lugar un espaciador de altura FIJA (abajo del header), la
               altura del header puede cambiar libremente sin mover una sola
               línea de contenido — lo único que cambia es cuánto tapa.
               El fondo es --paper-2 (antes blanco puro, el único elemento frío
               del sitio) y la sombra sale de --shadow-2, cálida y en capas.
               El filete de reposo va como `inset` box-shadow y NO como
               border-bottom: un borde suma al alto de la caja y obligaría al
               espaciador a compensar ese 1px; como sombra interior no ocupa
               nada y header y espaciador comparten exactamente las mismas
               clases de altura. */
            className="fixed top-0 left-0 right-0 z-50"
            style={{
                backgroundColor: scrolled ? 'color-mix(in srgb, var(--paper-2) 92%, transparent)' : 'var(--paper-2)',
                backdropFilter: scrolled ? 'blur(12px)' : 'none',
                boxShadow: scrolled ? 'var(--shadow-2)' : 'inset 0 -1px 0 var(--line)',
                transitionProperty: 'box-shadow, background-color',
                transitionDuration: 'var(--dur-base)',
                transitionTimingFunction: 'var(--ease-out-expo)',
            }}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* En móvil la cabecera en reposo baja de 144px a 96px: 144px
                    se comía el 18% de una pantalla de 812px. Al bajar, se
                    compacta todavía más. Animar `height` aquí es barato porque
                    el header está fuera del flujo — no provoca reflow del
                    documento, solo de su propio subárbol. */}
                <div
                    className={`flex items-center justify-between ${scrolled ? 'h-16 md:h-20' : 'h-24 md:h-36'}`}
                    style={{ transition: 'height var(--dur-base) var(--ease-out-expo)' }}
                >
                    {/* Logo */}
                    <Link to="/" data-testid="nav-logo" className="flex items-center group">
                        <img
                            src="/img/carpepel-assets/site_logo_opt.webp"
                            alt="Carpepel"
                            className={`w-auto object-contain ${scrolled ? 'h-12 md:h-14' : 'h-20 md:h-32'}`}
                            /* El archivo del logo trae su propio fondo BLANCO
                               opaco (no es PNG transparente). Mientras la navbar
                               era `bg-white` eso no se notaba; sobre --paper-2
                               (hueso) aparecía como un recuadro más claro
                               alrededor del logo. `multiply` resuelve justo este
                               caso: blanco × fondo = fondo (el recuadro
                               desaparece) y el águila negra se mantiene intacta
                               porque negro × cualquier cosa = negro. */
                            style={{
                                mixBlendMode: 'multiply',
                                transition: 'height var(--dur-base) var(--ease-out-expo)',
                            }}
                        />
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navLinks.map(link => (
                            <Link
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-${link.id}`}
                                /* El subrayado activo iba en `border-orange-500`:
                                   un naranja que no existe en la paleta. El
                                   acento del sitio es --kraft-deep. */
                                className="text-sm font-medium uppercase tracking-wider transition-colors"
                                style={{
                                    color: location.pathname === link.to ? 'var(--ink)' : 'var(--ink-soft)',
                                    borderBottom: location.pathname === link.to ? '2px solid var(--kraft-deep)' : '2px solid transparent',
                                    paddingBottom: '2px',
                                    transitionDuration: 'var(--dur-micro)',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ink)'; }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = location.pathname === link.to ? 'var(--ink)' : 'var(--ink-soft)';
                                }}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Right Actions */}
                    <div className="flex items-center gap-3">
                        {/* El carrito se muestra SIEMPRE: sin cuenta también se
                            puede armar, y sin ícono no habría forma de volver a él. */}
                        <Link
                            to="/carrito"
                            data-testid="nav-cart"
                            aria-label={cartCount > 0 ? `Carrito, ${cartCount} artículo${cartCount !== 1 ? 's' : ''}` : 'Carrito, vacío'}
                            className="relative p-2 hover:bg-[var(--paper)] rounded-sm transition-colors duration-200"
                        >
                            <ShoppingCart className="w-5 h-5" style={{ color: 'var(--ink)' }} />
                            {cartCount > 0 && (
                                <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 w-5 h-5 text-[10px] font-bold flex items-center justify-center rounded-sm" style={{ backgroundColor: 'var(--kraft-deep)', color: 'var(--paper)' }}>
                                    {cartCount}
                                </span>
                            )}
                        </Link>
                        {user ? (
                            <>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" data-testid="nav-user-menu" className="flex items-center gap-2 rounded-sm hover:bg-[var(--paper)]">
                                            <User className="w-4 h-4" />
                                            <span className="hidden sm:inline text-sm font-medium">{user.username}</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48 rounded-sm">
                                        <DropdownMenuItem data-testid="nav-orders" onClick={() => navigate('/pedidos')}>
                                            <Package className="w-4 h-4 mr-2" /> Mi Negocio
                                        </DropdownMenuItem>
                                        {isAdmin && (
                                            <DropdownMenuItem data-testid="nav-admin" onClick={() => navigate('/admin')}>
                                                <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Panel
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem data-testid="nav-logout" onClick={() => { logout(); navigate('/'); }}>
                                            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        ) : (
                            /* "Ingresar" deja de ser el botón más llamativo de la
                               barra: un botón negro lleno decía "primero entra"
                               justo donde la compra ya no lo exige. Queda como
                               enlace de texto (para clientes con cuenta y para el
                               admin) y en móvil se muda al menú. */
                            <Link
                                to="/auth"
                                data-testid="nav-login"
                                className="hidden md:inline-flex items-center gap-1.5 px-2 py-2 text-xs font-bold uppercase tracking-widest transition-colors duration-200 hover:text-[var(--ink)]"
                                style={{ color: 'var(--ink-soft)' }}
                            >
                                <User className="w-4 h-4" strokeWidth={1.75} /> Ingresar
                            </Link>
                        )}

                        {/* La acción principal del sitio, siempre a un toque. En
                            el catálogo y la ficha ya se está comprando: ahí el
                            botón sobraría y competiría con "Agregar". */}
                        {!['/catalogo', '/carrito', '/checkout'].includes(location.pathname) && !location.pathname.startsWith('/producto/') && !location.pathname.startsWith('/admin') && (
                            <Link
                                to="/catalogo"
                                data-testid="nav-buy"
                                className="inline-flex items-center h-9 px-4 md:px-5 rounded-sm text-[11px] md:text-xs font-bold uppercase tracking-widest whitespace-nowrap hover:opacity-85"
                                style={{ backgroundColor: 'var(--ink)', color: 'var(--paper)' }}
                            >
                                <span className="md:hidden">Comprar</span>
                                <span className="hidden md:inline">Comprar sin cuenta</span>
                            </Link>
                        )}

                        {/* Mobile menu toggle */}
                        {/* Solo lleva un icono dentro, así que sin aria-label un
                            lector de pantalla lo anunciaba como "botón" a secas.
                            aria-expanded comunica además si el menú está abierto. */}
                        <button
                            data-testid="nav-mobile-toggle"
                            aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
                            aria-expanded={mobileOpen}
                            className="md:hidden p-2 hover:bg-[var(--paper)] rounded-sm"
                            onClick={() => setMobileOpen(!mobileOpen)}
                        >
                            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile Nav */}
                {mobileOpen && (
                    <div className="md:hidden border-t border-[var(--line)] py-4 animate-slideDown">
                        {navLinks.map(link => (
                            <Link
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-mobile-${link.id}`}
                                className="block py-3 text-sm font-medium uppercase tracking-wider"
                                style={{ color: 'var(--ink-soft)' }}
                                onClick={() => setMobileOpen(false)}
                            >
                                {link.label}
                            </Link>
                        ))}
                        {!user && (
                            <Link
                                to="/auth"
                                data-testid="nav-mobile-login"
                                className="flex items-center gap-2 py-3 mt-1 border-t border-[var(--line)] text-sm font-medium uppercase tracking-wider"
                                style={{ color: 'var(--ink-soft)' }}
                                onClick={() => setMobileOpen(false)}
                            >
                                <User className="w-4 h-4" strokeWidth={1.75} /> Ingresar (opcional)
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </header>

        {/* Espaciador: ocupa en el flujo el sitio que la cabecera `fixed` ya no
            ocupa. Su altura es la de REPOSO del header y nunca cambia — ahí
            está el truco: el header puede encogerse al hacer scroll sin mover
            el contenido, porque lo que reserva el espacio es este div, no él.
            Las clases de altura deben seguir siendo las mismas que las del
            header en reposo (h-24 md:h-36). */}
        <div aria-hidden="true" className="h-24 md:h-36" data-testid="navbar-spacer" />
        </>
    );
}
